import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Search, UserCheck, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { sanitizeSearchQuery } from '@/utils/sanitize';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';

/** The fields a picked customer contributes to the lead form. */
export interface PickedCustomer {
  id: string;
  customer_type: string;
  customer_name: string;
  company_name: string | null;
  primary_phone: string;
  primary_email: string | null;
  primary_address: string;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  total_jobs_count: number | null;
}

const PICK_COLUMNS =
  'id, customer_type, customer_name, company_name, primary_phone, primary_email, primary_address, suburb, state, postcode, total_jobs_count';

/**
 * Name / phone / email search, capped and only while there is something to
 * search for. Deliberately not useCustomers(): with an empty query that hook
 * loads the whole customer table, which a form field must never do.
 */
function useCustomerSearch(search: string) {
  const safe = sanitizeSearchQuery(search.trim());
  return useQuery({
    queryKey: ['customer-picker', safe],
    enabled: safe.length >= 2,
    staleTime: 30_000,
    queryFn: async (): Promise<PickedCustomer[]> => {
      const { data, error } = await supabase
        .from('customers')
        .select(PICK_COLUMNS)
        .or(`customer_name.ilike.%${safe}%,company_name.ilike.%${safe}%,primary_phone.ilike.%${safe}%,primary_email.ilike.%${safe}%`)
        .order('last_contact_date', { ascending: false, nullsFirst: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as PickedCustomer[];
    },
  });
}

interface CustomerPickerProps {
  /** The customer this lead is linked to, or null for a fresh contact. */
  linked: Pick<PickedCustomer, 'id' | 'customer_name' | 'primary_phone' | 'company_name'> | null;
  onSelect: (customer: PickedCustomer) => void;
  onClear: () => void;
  disabled?: boolean;
}

/**
 * Attach the lead to an existing customer instead of keying their details
 * again. Picking one fills the contact fields and pins customer_id, so the
 * lead lands on that customer's card whatever phone or email is typed after.
 */
export function CustomerPicker({ linked, onSelect, onClear, disabled = false }: CustomerPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search, 250);
  const { data: results = [], isFetching } = useCustomerSearch(debounced);

  if (linked) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
        <div className="flex min-w-0 items-center gap-2">
          <UserCheck className="h-4 w-4 shrink-0 text-primary" />
          <span className="truncate">
            Existing customer: <span className="font-medium">{linked.customer_name}</span>
            {linked.company_name ? <span className="text-muted-foreground"> · {linked.company_name}</span> : null}
            <span className="text-muted-foreground"> · {linked.primary_phone}</span>
          </span>
        </div>
        <Button type="button" variant="ghost" size="sm" className="h-7 shrink-0 gap-1 px-2" onClick={onClear} disabled={disabled}>
          <X className="h-3.5 w-3.5" /> Unlink
        </Button>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal text-muted-foreground"
        >
          <span className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            Existing customer? Search by name, phone or email
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        {/* Filtering is server-side; cmdk must not re-filter by its own text match. */}
        <Command shouldFilter={false}>
          <CommandInput placeholder="Type at least 2 characters…" value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>
              {debounced.trim().length < 2
                ? 'Start typing to search.'
                : isFetching
                  ? 'Searching…'
                  : 'No matching customer. Fill in the details below to create a new one.'}
            </CommandEmpty>
            {results.length > 0 && (
              <CommandGroup heading="Customers">
                {results.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={c.id}
                    onSelect={() => {
                      onSelect(c);
                      setOpen(false);
                      setSearch('');
                    }}
                    className="flex items-start gap-2"
                  >
                    <Check className={cn('mt-0.5 h-4 w-4 shrink-0', linked ? 'opacity-100' : 'opacity-0')} />
                    <div className="min-w-0">
                      <div className="truncate font-medium">
                        {c.customer_name}
                        {c.company_name ? <span className="text-muted-foreground"> · {c.company_name}</span> : null}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {c.primary_phone}
                        {c.primary_email ? ` · ${c.primary_email}` : ''}
                        {c.total_jobs_count ? ` · ${c.total_jobs_count} job${c.total_jobs_count === 1 ? '' : 's'}` : ''}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
